/**
 * Google geocode adapter — uses the Geocoder class from the Maps JS API
 * (not the REST Geocoding endpoint).
 *
 * Why JS API rather than REST: Google's HTTP-referrer-restricted browser keys
 * (which we use everywhere) are **rejected** by the Geocoding REST endpoint
 * with `REQUEST_DENIED: API keys with referer restrictions cannot be used
 * with this API.` The Geocoder class accessed through the loaded Maps JS API
 * shares the same loader and accepts referrer-restricted keys. Same billing
 * SKU, same key, just a different transport.
 *
 * Loading: relies on `<APIProvider>` (mounted by GoogleMapView /
 * GoogleAutocomplete) having bootstrapped Maps JS. After that,
 * `google.maps.importLibrary('geocoding')` is the canonical way to obtain
 * the Geocoder class. If `importLibrary` is unavailable (geocode called
 * before any map mounted), we surface a warning and return null — caller
 * sees the same `null` they'd get from a ZERO_RESULTS response.
 *
 * Municipio resolution: Google's `administrative_area_level_1` for a PR
 * address is the municipio (e.g. "Bayamón", "San Juan"). Canonical names
 * use Unicode accents (ñ, á, ó, í) — Google returns them already encoded
 * that way, so a direct string match against TWM's `municipalities` seed
 * data works for the common case. For edge cases (`Municipio de X`,
 * accent-stripped variants), we fall through to a name-normalizing map
 * keyed to the canonical seed list.
 *
 * See [[shared/maps/types.ts]] for the GeocodeConnector contract.
 */

import type { GeocodeConnector, ForwardGeocodeOptions } from '../../types';

// Canonical municipio names from `backend/scripts/seed_municipalities.py`.
// Keep in sync with that file (78 entries). Source of truth is the seed
// script — this is a lookup table for normalizing Google's response strings
// back to the exact name the `/rolloff-rates/lookup` endpoint expects.
const CANONICAL_MUNICIPALITIES: ReadonlySet<string> = new Set([
  'Adjuntas', 'Aguada', 'Aguadilla', 'Aguas Buenas', 'Aibonito', 'Añasco',
  'Arecibo', 'Arroyo', 'Barceloneta', 'Barranquitas', 'Bayamón', 'Cabo Rojo',
  'Caguas', 'Camuy', 'Canóvanas', 'Carolina', 'Cataño', 'Cayey', 'Ceiba',
  'Ciales', 'Cidra', 'Coamo', 'Comerío', 'Corozal', 'Culebra', 'Dorado',
  'Fajardo', 'Florida', 'Guánica', 'Guayama', 'Guayanilla', 'Guaynabo',
  'Gurabo', 'Hatillo', 'Hormigueros', 'Humacao', 'Isabela', 'Jayuya',
  'Juana Díaz', 'Juncos', 'Lajas', 'Lares', 'Las Marías', 'Las Piedras',
  'Loíza', 'Luquillo', 'Manatí', 'Maricao', 'Maunabo', 'Mayagüez', 'Moca',
  'Morovis', 'Naguabo', 'Naranjito', 'Orocovis', 'Patillas', 'Peñuelas',
  'Ponce', 'Quebradillas', 'Rincón', 'Río Grande', 'Sabana Grande', 'Salinas',
  'San Germán', 'San Juan', 'San Lorenzo', 'San Sebastián', 'Santa Isabel',
  'Toa Alta', 'Toa Baja', 'Trujillo Alto', 'Utuado', 'Vega Alta', 'Vega Baja',
  'Vieques', 'Villalba', 'Yabucoa', 'Yauco',
]);

const NORMALIZED_TO_CANONICAL: ReadonlyMap<string, string> = (() => {
  const map = new Map<string, string>();
  for (const name of CANONICAL_MUNICIPALITIES) {
    map.set(normalizeForLookup(name), name);
  }
  return map;
})();

function normalizeForLookup(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/^municipio\s+(de|of)\s+/, '')
    .trim();
}

function toCanonicalMunicipality(raw: string | undefined | null): string | null {
  if (!raw) return null;
  if (CANONICAL_MUNICIPALITIES.has(raw)) return raw;
  const trimmed = raw.trim();
  if (CANONICAL_MUNICIPALITIES.has(trimmed)) return trimmed;
  return NORMALIZED_TO_CANONICAL.get(normalizeForLookup(trimmed)) ?? null;
}

// Open Location Code (Plus Code) token, e.g. "MW9F+4F" or the full global form
// "796RWF8Q+WF". Plus Codes draw from a fixed 20-char alphabet (vowels and a
// few ambiguous letters excluded). Google prepends one to `formatted_address`
// whenever a location has no street number, which reads as gibberish on an
// invoice — strip it and keep whatever real place text follows.
const PLUS_CODE_TOKEN = /\b[23456789CFGHJMPQRVWX]{4,8}\+[23456789CFGHJMPQRVWX]{2,3}\b/i;

function stripPlusCode(address: string): string {
  return address
    .replace(PLUS_CODE_TOKEN, '')
    .replace(/\s*,\s*,+/g, ', ') // collapse the empty segment the strip leaves
    .replace(/^[\s,]+/, '')
    .trim();
}

function extractMunicipality(result: google.maps.GeocoderResult | undefined): string | null {
  if (!result) return null;
  const components = result.address_components ?? [];
  const candidateTypes = [
    'administrative_area_level_1',
    'locality',
    'administrative_area_level_2',
    'sublocality',
    'sublocality_level_1',
  ];
  for (const type of candidateTypes) {
    for (const comp of components) {
      if (!comp.types.includes(type)) continue;
      const canonical = toCanonicalMunicipality(comp.long_name)
        ?? toCanonicalMunicipality(comp.short_name);
      if (canonical) return canonical;
    }
  }
  return null;
}

// Cache the Geocoder instance once Maps JS is loaded so we don't re-import
// the library on every call.
let geocoderPromise: Promise<google.maps.Geocoder | null> | null = null;

async function getGeocoder(): Promise<google.maps.Geocoder | null> {
  if (typeof window === 'undefined') return null;
  if (geocoderPromise) return geocoderPromise;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const gmaps = (window as any).google?.maps;
  if (!gmaps) {
    console.warn(
      '[maps:google] Geocoder requested before Maps JS loaded — ensure a MapView or Autocomplete is mounted (APIProvider must bootstrap first).',
    );
    return null;
  }
  geocoderPromise = (async () => {
    try {
      if (typeof gmaps.importLibrary === 'function') {
        const lib = await gmaps.importLibrary('geocoding') as google.maps.GeocodingLibrary;
        return new lib.Geocoder();
      }
      if (gmaps.Geocoder) return new gmaps.Geocoder();
      console.error('[maps:google] no Geocoder class on google.maps — Maps JS may be partially loaded.');
      return null;
    } catch (err) {
      console.error('[maps:google] importLibrary(geocoding) failed:', err);
      return null;
    }
  })();
  return geocoderPromise;
}

// Cache the Places library import (for getPlaceDetails) the same way the
// Geocoder is cached — both ride the APIProvider-bootstrapped Maps JS loader.
let placesLibPromise: Promise<google.maps.PlacesLibrary | null> | null = null;

async function getPlacesLib(): Promise<google.maps.PlacesLibrary | null> {
  if (typeof window === 'undefined') return null;
  if (placesLibPromise) return placesLibPromise;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const gmaps = (window as any).google?.maps;
  if (!gmaps || typeof gmaps.importLibrary !== 'function') {
    console.warn(
      '[maps:google] Places library requested before Maps JS loaded — ensure a MapView or Autocomplete is mounted (APIProvider must bootstrap first).',
    );
    return null;
  }
  placesLibPromise = (async () => {
    try {
      return (await gmaps.importLibrary('places')) as google.maps.PlacesLibrary;
    } catch (err) {
      console.error('[maps:google] importLibrary(places) failed:', err);
      return null;
    }
  })();
  return placesLibPromise;
}

interface GeocodeError {
  code?: string;
  message?: string;
}

async function geocodeViaJsApi(
  request: google.maps.GeocoderRequest,
): Promise<google.maps.GeocoderResult[] | null> {
  const geocoder = await getGeocoder();
  if (!geocoder) return null;
  try {
    const response = await geocoder.geocode(request);
    return response.results ?? null;
  } catch (err) {
    const code = (err as GeocodeError).code;
    // Geocoder.geocode rejects with `{code: 'ZERO_RESULTS'}` when nothing
    // matched. That's a valid empty case, not an error worth surfacing.
    if (code === 'ZERO_RESULTS') return null;
    console.error(`[maps:google] geocode status=${code ?? 'UNKNOWN'}`, (err as GeocodeError).message ?? err);
    return null;
  }
}

export const googleGeocodeConnector: GeocodeConnector = {
  async reverseGeocode(lat, lng) {
    const results = await geocodeViaJsApi({ location: { lat, lng } });
    if (!results || results.length === 0) return null;
    // For a bare coordinate at an unnamed spot (common for rural PR service
    // locations), Google's top result is a Plus Code — e.g. "MW9F+4F San Juan,
    // Puerto Rico" — which is meaningless on an invoice. Prefer the first result
    // that isn't a Plus Code entry (Google orders results most-specific first,
    // so this walks street address → route → neighborhood → locality). Then
    // strip any Plus Code token Google prepends to a street-less address. If
    // nothing human-readable survives, fall back to the raw coordinate.
    const top = results.find((r) => !r.types?.includes('plus_code')) ?? results[0];
    const formatted = stripPlusCode(top.formatted_address ?? '')
      || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    const placeName = top.address_components.find((c) =>
      c.types.some((t) =>
        ['point_of_interest', 'establishment', 'premise', 'locality'].includes(t),
      ),
    )?.long_name;
    return { formatted, placeName };
  },

  async reverseGeocodeMunicipality(lat, lng) {
    const results = await geocodeViaJsApi({
      location: { lat, lng },
      region: 'pr',
      language: 'es',
    });
    if (!results || results.length === 0) return null;
    for (const r of results) {
      const muni = extractMunicipality(r);
      if (muni) return muni;
    }
    return null;
  },

  async forwardGeocode(query, opts: ForwardGeocodeOptions = {}) {
    const country = (opts.country ?? 'PR').toUpperCase();
    const results = await geocodeViaJsApi({
      address: query,
      region: country.toLowerCase(),
      componentRestrictions: { country },
    });
    if (!results || results.length === 0) return null;
    const top = results[0];
    const loc = top.geometry?.location;
    if (!loc) return null;
    return {
      lat: loc.lat(),
      lng: loc.lng(),
      address: top.formatted_address,
      placeId: top.place_id,
    };
  },

  async getPlaceDetails(placeId) {
    const lib = await getPlacesLib();
    if (!lib) return null;
    try {
      const place = new lib.Place({ id: placeId });
      await place.fetchFields({ fields: ['location', 'formattedAddress', 'displayName'] });
      const loc = place.location;
      if (!loc) return null;
      return {
        lat: loc.lat(),
        lng: loc.lng(),
        address: place.formattedAddress ?? `${loc.lat().toFixed(6)}, ${loc.lng().toFixed(6)}`,
        placeName: place.displayName ?? undefined,
      };
    } catch (err) {
      console.error('[maps:google] place details fetchFields failed:', err);
      return null;
    }
  },
};
